import {
  action,
  computed,
  makeObservable,
  onBecomeObserved,
  reaction,
} from "mobx"

import { catchErrors, massPayoutsApi } from "@src/shared/api"
import {
  MassPayoutJsonCreate,
  MassPayoutResponse,
  MassPayoutShort,
  NetworkCrypto,
  SymbolCrypto,
} from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"
import { filtersPresets } from "@src/shared/componemts/TableFilter/classes/constants"

export type MassPayoutsCollectionLoadRequest = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  externalId?: string
  order?: "asc" | "desc"
  page?: number
}
export type MassPayoutsCollectionFilter = {
  date?: {
    dateFrom?: string
    dateTo?: string
  }
  externalId?: string
  order?: "asc" | "desc"
}
import axios from "axios"

import { ManualPayoutsFormType } from "@src/pages/massPayouts/crypto/ui/manual/ManualPayoutsForm"
import { API_PATH } from "@src/shared/config"

export class MassPayoutsCollection extends loading(
  ["fetch", "createMassPayoutJson", "createMassPayoutFormData"],
  CollectionWithPagination<MassPayoutShort>,
) {
  constructor() {
    super()

    makeObservable(this, {
      _create: action.bound,
      fetch: action.bound,
      filter: computed,
    })

    onBecomeObserved(this, "isLoading", this.load)

    reaction(
      () => this.filter.state,
      (v) => v && this.load(v),
    )
  }

  async _create(
    request: FormData | MassPayoutJsonCreate,
  ): Promise<MassPayoutResponse | null> {
    return axios
      .post(`${API_PATH}/v2.0/mass-payouts`, request)
      .then(({ data: { data } = {} }) => data)
  }

  async createMassPayoutFormData(form: FormData) {
    return this._create(form)
  }

  async createMassPayoutJson(req: ManualPayoutsFormType) {
    const { fee_payer, payouts } = req

    return this._create({
      fee_payer,
      id: crypto.randomUUID(),
      payouts: payouts.map(({ currency, ...rest }) => {
        const [symbol, network] = currency.split(",") as [
          SymbolCrypto,
          NetworkCrypto,
        ]

        return { ...rest, network, symbol }
      }),
      with_confirmation: true,
    })
  }

  async fetch(req: MassPayoutsCollectionLoadRequest) {
    try {
      const { data: { data } = {} } = await massPayoutsApi.listMassPayouts(
        req.externalId,
        req.date?.dateFrom,
        req.date?.dateTo,
        req.order,
        this.calcOffset(req.page),
        this.pageSize,
      )

      if (data) {
        return {
          data: data.data!,
          pagination: data.pagination!,
        }
      }
    } catch (error: any) {
      catchErrors(error)
    }

    return null
  }

  get filter() {
    return new CollectionFilters<MassPayoutsCollectionFilter>([
      CollectionFilters.createField("externalId").search({
        col: 6,
        label: "Search for External ID",
        placeholder: "External ID",
      }),
      CollectionFilters.createField("date").date({ col: 8, label: "Date" }),
      {
        ...filtersPresets.order,
        col: 4,
        label: "Select on order",
        placeholder: "select",
      },
    ])
  }
}
